'use strict';

// external imports
import React, {
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import parse from 'html-react-parser';

// prime react imports
import TreeNode from 'primereact/treenode'
import {Button} from 'primereact/button';
import {ChevronDownIcon} from 'primereact/icons/chevrondown';
import {ChevronRightIcon} from 'primereact/icons/chevronright';
import {Dropdown} from 'primereact/dropdown';
import {IconUtils} from 'primereact/utils';
import {Panel} from 'primereact/panel';
import {Tooltip} from 'primereact/tooltip';
import {TreeTableExpandedKeysType} from 'primereact/treetable';
import {Tree} from 'primereact/tree';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Reference from '~/shared/src/ci/Reference';

// frontend components import
import ContentButton, {ReferenceType as ContentButtonType} from '~/frontend/src/components/base/shared/ContentButton';
import SearchField from '~/frontend/src/components/base/shared/field/SearchField';

// frontend imports
import {
    ApplicationToYnItemTypeItemIdMapping,
    YnItemType,
    YnReferenceSet
} from '~/frontend/src/modules/panels/MetaModelDesignerView';
import {markFilterSearchesInText} from '~/frontend/src/utilities/renderHelpers';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import updateReference from '~/frontend/src/actions/data/updateReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';


type ExtendTreeNode = TreeNode.TreeNode &
    (
        {
            ynItemTypeItemId : number;
            ynReferenceSetId? : never;
        } |
        {
            ynItemTypeItemId? : never;
            ynReferenceSetId : number;
        }
        ) &
    {
        key : string;
        label : string;
        deleted : boolean;
        dirty : boolean;
        error? : {
            count : number;
            tooltip? : React.ReactNode;
        };
        buttons? : React.ReactNode[];
    };


export type DefaultProps = {
    schema? : Schema;

    readOnly? : boolean;

    ynItemTypes? : YnItemType[];
    ynReferenceSets? : YnReferenceSet[];
    applicationToYnItemTypeItemIdMapping? : ApplicationToYnItemTypeItemIdMapping;
    showApplicationDropdown? : boolean;
    showSearchStringField? : boolean;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    expandedKeys? : TreeTableExpandedKeysType;

    scrollTop? : number;
    scrollLeft? : number;
};
export const defaultProps : Required<DefaultProps> = {
    schema                              : 'DATA',
    readOnly                            : true,
    ynItemTypes                         : [],
    ynReferenceSets                     : [],
    applicationToYnItemTypeItemIdMapping: {},
    showApplicationDropdown             : true,
    showSearchStringField               : true,
    additionalElusiveDataContexts       : [],
    expandedKeys                        : {},
    scrollTop                           : 0,
    scrollLeft                          : 0
};

export type Props = {
    uid : string;

    dataContext : string;

    searchString? : string;

    application? : string;
    onSelectApplication? : ( application? : string ) => void;

    highlightYnReferenceSetId? : number;
    onHighlightYnReferenceSetId? : ( id? : number ) => void;

    selectedYnReferenceSetId? : number;
    onSelectYnReferenceSetId? : ( id? : number ) => void;

    highlightYnItemTypeId? : number;
    onHighlightYnItemTypeId? : ( id? : number ) => void;

    selectedYnItemTypeId? : number;
    onSelectYnItemTypeId? : ( id? : number ) => void;

    onResetFormData : ( itemId : number ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {};

type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    treeData? : ExtendTreeNode[];
    scrollPositions? : {
        [ treeNodeDataKey : string ] : number;
    }
};


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const DesignerTree = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch          = useDispatch(),
          {
              additionalElusiveDataContexts,
              application,
              applicationToYnItemTypeItemIdMapping,
              dataContext,
              expandedKeys,
              highlightYnItemTypeId,
              highlightYnReferenceSetId,
              onHighlightYnItemTypeId,
              onHighlightYnReferenceSetId,
              onResetFormData,
              onSelectApplication,
              onSelectYnItemTypeId,
              onSelectYnReferenceSetId,
              readOnly,
              schema,
              scrollLeft,
              scrollTop,
              searchString,
              selectedYnItemTypeId,
              selectedYnReferenceSetId,
              showApplicationDropdown,
              showSearchStringField,
              uid,
              ynItemTypes,
              ynReferenceSets
          }                               = props,
          user                            = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp         = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp              = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          elusiveData                     = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          lastParams                      = useRef<any>( null ),
          initialRendered                 = useRef<boolean>( false ),
          treeElement                     = useRef<HTMLDivElement | null>( null ),
          nodeButtons                     = useRef<{
              [ buttonId : string ] : { justInteracted : boolean, content? : ContentButtonType }
          }>( {} ),
          nodeButtonPanels                = useRef<{ [ panelId : string ] : Panel }>( {} ),
          ignoreSelectionChange           = useRef<boolean>( false ),
          language                        = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          applications                    = useMemo(
              () => {
                  return Object.keys( applicationToYnItemTypeItemIdMapping ) as string[];
              },
              [
                  applicationToYnItemTypeItemIdMapping
              ]
          ),
          filteredItemTypeIds             = useMemo(
              () => {
                  return application
                      ? Object.keys( applicationToYnItemTypeItemIdMapping[ application ] ).map( i => Number( i ) )
                      : undefined
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping
              ]
          ),
          filteredReferenceSets           = useMemo(
              () => {
                  const retVal : YnReferenceSet[] = [];

                  for ( const referenceSet of ynReferenceSets ) {
                      if (
                          (
                              !application
                              || (
                                  filteredItemTypeIds
                                  && filteredItemTypeIds.includes( referenceSet.sourceId )
                                  && referenceSet.targetId
                                  && filteredItemTypeIds.includes( referenceSet.targetId )
                              )
                          )
                          && (
                              !searchString
                              || referenceSet.fullLabel.toLowerCase().indexOf( searchString.toLowerCase() ) !== -1
                          )
                      ) {
                          retVal.push( {
                              ...referenceSet,
                              fullLabel: searchString
                                  ? markFilterSearchesInText( referenceSet.fullLabel, searchString )
                                  : referenceSet.fullLabel
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping,
                  ynReferenceSets,
                  searchString
              ]
          ),
          filteredItemTypes               = useMemo(
              () => {
                  const retVal : YnItemType[]                                   = [],
                        filteredYnItemTypesFromFilteredReferenceSets : number[] = [];

                  for ( const referenceSet of filteredReferenceSets ) {
                      if ( !filteredYnItemTypesFromFilteredReferenceSets.includes( referenceSet.sourceId ) ) {
                          filteredYnItemTypesFromFilteredReferenceSets.push( referenceSet.sourceId );
                      }
                      if ( referenceSet.targetId && !filteredYnItemTypesFromFilteredReferenceSets.includes( referenceSet.targetId ) ) {
                          filteredYnItemTypesFromFilteredReferenceSets.push( referenceSet.targetId );
                      }
                  }

                  for ( const itemType of ynItemTypes ) {
                      if (
                          filteredYnItemTypesFromFilteredReferenceSets.includes( itemType.id )
                          || (
                              (
                                  !application
                                  || (
                                      filteredItemTypeIds
                                      && filteredItemTypeIds.includes( itemType.id )
                                  )
                              )
                              && (
                                  !searchString
                                  || itemType.label.toLowerCase().indexOf( searchString.toLowerCase() ) !== -1
                              )
                          )
                      ) {
                          retVal.push( {
                              ...itemType,
                              label  : searchString
                                  ? markFilterSearchesInText( itemType.label, searchString )
                                  : itemType.label,
                              deleted: application
                                  ? itemType.deleted && !applicationToYnItemTypeItemIdMapping[ application ]?.[ itemType.id ]
                                  : itemType.deleted
                          } );
                      }
                  }

                  return retVal;
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping,
                  filteredReferenceSets,
                  ynItemTypes,
                  searchString
              ]
          ),
          addYnReferenceSet               = ( ynItemType : YnItemType, event : React.MouseEvent ) => {
              const activeStatusId                                        = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                    ynReferenceSetItemTypeId                              = metaModel[ 'CFG' ].getItemType( 'YnReferenceSet' )?.id,
                    ynReferenceSetModulePropertySetId                     = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnReferenceSet', 'Module' )?.id,
                    ynReferenceSetOriginPropertySetId                     = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnReferenceSet', 'Origin' )?.id,
                    ynReferenceSetSchemaPropertySetId                     = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'YnReferenceSet', 'Schema' )?.id,
                    ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isSourceObjectType', 'src' )[ 0 ]?.id ?? 0;

              if ( ynReferenceSetItemTypeId && ynReferenceSetModulePropertySetId && ynReferenceSetOriginPropertySetId && ynReferenceSetSchemaPropertySetId ) {
                  const ynReferenceSetItem = dispatch( addItem(
                      dataContext,
                      {
                          itemTypeId: ynReferenceSetItemTypeId,
                          statusId  : activeStatusId,
                          name      : 'new reference set',
                          properties: [
                              {
                                  propertySetId: ynReferenceSetModulePropertySetId,
                                  value        : 'base',
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: ynReferenceSetOriginPropertySetId,
                                  value        : global.config.common.uuidOrigin,
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: ynReferenceSetSchemaPropertySetId,
                                  value        : schema.toLowerCase(),
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG',
                      additionalElusiveDataContexts
                  ) );

                  if ( ynReferenceSetItem ) {
                      if ( ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId ) {
                          dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: ynItemTypeIsSrcObjectTypeYnReferenceSetReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : ynItemType.id,
                                  destItemId    : ynReferenceSetItem.id
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );
                      }

                      onSelectYnItemTypeId?.();
                      onSelectYnReferenceSetId?.( ynReferenceSetItem.id );
                  }
              }

              event.stopPropagation();
          },
          deleteOrUndeleteYnItemType      = ( event : React.MouseEvent, ynItemType : YnItemType, referenceOnly : boolean = false ) => {
              const ynItemTypeItem                               = global.data[ dataContext ].items[ ynItemType.id ],
                    ynItemTypeBelongsToApplicationReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'Application', 'belongsTo', 'src' )[ 0 ]?.id ?? 0;

              let wasDeleted : boolean = false;

              if ( referenceOnly && application ) {
                  let reference : Reference | undefined = undefined;

                  for ( const referenceId of ynItemTypeItem.references ) {
                      reference = global.data[ dataContext ].references[ referenceId ];

                      if ( reference.referenceSetId === ynItemTypeBelongsToApplicationReferenceSetId ) {
                          const referencedItem = global.data[ dataContext ].items[ reference.destItemId ];

                          if ( referencedItem.name === application ) {
                              break;
                          }
                      }

                      reference = undefined;
                  }

                  if ( reference?.isDeleted() ) {
                      dispatch( updateReference(
                          dataContext,
                          reference.id,
                          { statusId: reference.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }
                  else if ( reference ) {
                      wasDeleted = true;

                      dispatch( deleteReference(
                          dataContext,
                          reference.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }
              }
              else {
                  const ynItemTypeItemDeleted = ynItemTypeItem.isDeleted();

                  // undelete
                  if ( ynItemTypeItemDeleted ) {
                      dispatch( updateItem(
                          dataContext,
                          ynItemType.id,
                          { statusId: ynItemTypeItem.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }
                  else {
                      wasDeleted = true;

                      dispatch( deleteItem(
                          dataContext,
                          ynItemType.id,
                          true,
                          additionalElusiveDataContexts,
                          false,
                          ynItemType.id < 0
                      ) );
                  }
              }

              if ( wasDeleted ) {
                  if ( expandedKeys[ `it_${ynItemType.id}` ] ) {
                      const newExpandedKeys = { ...expandedKeys };

                      delete newExpandedKeys[ `it_${ynItemType.id}` ];

                      dispatch( setParams( uid, { expandedKeys: newExpandedKeys } ) );
                  }

                  onResetFormData( ynItemType.id );

                  if ( ynItemType.id === lastParams.current?.selectedYnItemTypeId ) {
                      onSelectYnItemTypeId?.( undefined );
                  }

                  if ( ynItemType.id === lastParams.current?.highlightYnItemTypeId ) {
                      onHighlightYnItemTypeId?.( undefined );
                  }
              }

              event.stopPropagation();
          },
          deleteOrUndeleteYnReferenceSet  = ( event : React.MouseEvent, ynReferenceSet : YnReferenceSet ) => {
              const ynReferenceSetItem        = global.data[ dataContext ].items[ ynReferenceSet.id ],
                    ynReferenceSetItemDeleted = ynReferenceSetItem.isDeleted();

              // undelete
              if ( ynReferenceSetItemDeleted ) {
                  dispatch( updateItem(
                      dataContext,
                      ynReferenceSet.id,
                      { statusId: ynReferenceSetItem.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }
              else {
                  dispatch( deleteItem(
                      dataContext,
                      ynReferenceSet.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      ynReferenceSet.id < 0
                  ) );

                  onResetFormData( ynReferenceSet.id );

                  if ( ynReferenceSet.id === lastParams.current?.selectedYnReferenceSetId ) {
                      onSelectYnReferenceSetId?.( undefined );
                  }

                  if ( ynReferenceSet.id === lastParams.current?.highlightYnReferenceSetId ) {
                      onHighlightYnReferenceSetId?.( undefined );
                  }
              }

              event.stopPropagation();
          },
          buildAddYnReferenceSetButton    = ( ynItemType : YnItemType ) => {
              const buttonId = `${uid}_iT_${ynItemType.id}_addButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              if (
                  readOnly
                  || ynItemType.deleted
                  || (
                      application
                      && applicationToYnItemTypeItemIdMapping[ application ]?.[ ynItemType.id ] === false
                  )
              ) {
                  return <Button
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: undefined
                              };
                          }
                      }}

                      icon={'pi pi-plus-circle yn-deleted'}
                      rounded={true}
                      text={true}
                      disabled={true}
                  />;
              }
              else {
                  return <Button
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: undefined
                              };
                          }
                      }}

                      onClick={addYnReferenceSet.bind( null, ynItemType )}
                      icon={'pi pi-plus-circle yn-add'}
                      rounded={true}
                      text={true}
                  />;
              }
          },
          buildDeleteYnItemTypeButton     = ( ynItemType : YnItemType ) => {
              const buttonId = `${uid}_iT_${ynItemType.id}_deleteButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              // disabled delete button
              if (
                  readOnly
                  || (
                      !application
                      && !ynItemType.deletable
                  )
              ) {
                  return <Button
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: undefined
                              };
                          }
                      }}

                      icon={'pi pi-minus-circle yn-deleted'}
                      rounded={true}
                      text={true}
                      disabled={true}
                  />;
              }

              // disconnect-only button
              else if (
                  application
                  && !ynItemType.deletable
                  && applicationToYnItemTypeItemIdMapping[ application ]?.[ ynItemType.id ] !== false
              ) {
                  return <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={'pi pi-minus-circle yn-delete'}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      onExpand={( event ) => {
                          let innerRetVal : boolean = true;

                          if ( event.altKey ) {
                              deleteOrUndeleteYnItemType( event, ynItemType, true );

                              innerRetVal = false;
                          }

                          ignoreSelectionChange.current = true;

                          event.stopPropagation();
                          event.preventDefault();

                          return innerRetVal;
                      }}

                      content={<Panel
                          key={`${buttonId}_panel`}
                          id={`${buttonId}_panel`}

                          style={{ width: '25rem' }}
                          className="yn-confirm-option-panel"
                      >
                          <label>
                              <span className="pi pi-question-circle yn-blue"></span>
                              {__( 'YN.base.frontend.metaModelDesigner.itemType.deleteReferenceToApplication.confirmation', { itemType: ynItemType.label } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={() => {
                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={( event ) => {
                                  deleteOrUndeleteYnItemType( event, ynItemType, true );

                                  ignoreSelectionChange.current = true;

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>}
                  />;
              }

              // un-disconnect-only button
              else if (
                  application
                  && applicationToYnItemTypeItemIdMapping[ application ]?.[ ynItemType.id ] === false
              ) {
                  return <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={'pi pi-plus-circle yn-dirty'}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      onExpand={( event ) => {
                          let innerRetVal : boolean = true;

                          deleteOrUndeleteYnItemType( event, ynItemType, true );

                          innerRetVal = false;

                          ignoreSelectionChange.current = true;

                          event.stopPropagation();
                          event.preventDefault();

                          return innerRetVal;
                      }}

                      content={null}
                  />;
              }

              // normal delete / restore button
              else if (
                  !application
                  || ynItemType.deleted
              ) {
                  return <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={`pi ${ynItemType.deleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      onExpand={( event ) => {
                          let innerRetVal : boolean = true;

                          if ( event.altKey || ynItemType.deleted ) {
                              deleteOrUndeleteYnItemType( event, ynItemType );

                              innerRetVal = false;
                          }

                          ignoreSelectionChange.current = true;

                          event.stopPropagation();
                          event.preventDefault();

                          return innerRetVal;
                      }}

                      content={!ynItemType.deleted
                          ? <Panel
                              key={`${buttonId}_panel`}
                              id={`${buttonId}_panel`}

                              style={{ width: '25rem' }}
                              className="yn-confirm-option-panel"
                          >
                              <label>
                                  <span className="pi pi-question-circle yn-blue"></span>
                                  {__( 'YN.base.frontend.metaModelDesigner.itemType.delete.text', { itemType: ynItemType.label } )}
                              </label>
                              <Button
                                  label={__( 'YN.base.common.no' )}
                                  className="p-button-sm p-button-secondary mr-3"
                                  rounded={false}
                                  text={false}
                                  outlined={true}
                                  onClick={() => {
                                      ignoreSelectionChange.current = true;

                                      nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                  }}
                              />
                              <Button
                                  label={__( 'YN.base.common.yes' )}
                                  className="p-button-sm p-confirm-popup-accept"
                                  rounded={false}
                                  text={false}
                                  outlined={true}
                                  onClick={( event ) => {
                                      deleteOrUndeleteYnItemType( event, ynItemType );

                                      ignoreSelectionChange.current = true;

                                      nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                  }}
                              />
                          </Panel>
                          : null}
                  />;
              }

              // disconnect or delete button
              else {
                  const buttonContent : React.ReactNode[] = [],
                        key1                              = `${buttonId}_disconnect`,
                        key2                              = `${buttonId}_delete`;

                  delete nodeButtonPanels.current[ key1 ];
                  delete nodeButtonPanels.current[ key2 ];

                  // delete reference to application
                  buttonContent.push(
                      <Panel
                          key={key1}
                          id={`${uid}_${key1}_panel`}

                          ref={node => {
                              if ( node ) {
                                  nodeButtonPanels.current[ key1 ] = node;
                              }
                          }}

                          header={
                              <Button
                                  label={__( 'YN.base.frontend.metaModelDesigner.itemType.deleteReferenceToApplication.label' )}
                                  icon={'pi pi-minus-circle yn-delete'}
                                  rounded={false}
                                  text={true}
                                  className="yn-menu-button yn-no-rounded-borders"
                                  onClick={( event ) => {
                                      if ( event.altKey ) {
                                          deleteOrUndeleteYnItemType( event, ynItemType, true );

                                          nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                      }
                                      else {
                                          for ( const dPanelId of Object.keys( nodeButtonPanels.current ) ) {
                                              if ( dPanelId !== key1 ) {
                                                  nodeButtonPanels.current[ dPanelId ].collapse( event );
                                              }
                                              else {
                                                  nodeButtonPanels.current[ dPanelId ].toggle( event );
                                              }
                                          }
                                      }
                                  }}
                              />
                          }

                          toggleable={true}
                          collapsed={true}
                          className="yn-confirm-option-panel"
                          pt={{
                              root   : {
                                  style: {
                                      padding: '0'
                                  }
                              },
                              content: {
                                  className: 'yn-confirm-option-panel-content'
                              }
                          }}
                      >
                          <label>
                              <span className="pi pi-question-circle"></span>
                              {__( 'YN.base.frontend.metaModelDesigner.itemType.deleteReferenceToApplication.confirmation', { itemType: ynItemType.label } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={( event ) => {
                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={( event ) => {
                                  deleteOrUndeleteYnItemType( event, ynItemType, true );

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                  );

                  // delete ynItemType
                  buttonContent.push(
                      <Panel
                          key={key2}
                          id={`${uid}_${key2}_panel`}

                          ref={node => {
                              if ( node ) {
                                  nodeButtonPanels.current[ key2 ] = node;
                              }
                          }}

                          header={
                              <Button
                                  label={__( 'YN.base.frontend.metaModelDesigner.itemType.delete.label' )}
                                  icon={'pi pi-minus-circle yn-delete'}
                                  rounded={false}
                                  text={true}
                                  className="yn-menu-button yn-no-rounded-borders"
                                  onClick={( event ) => {
                                      if ( event.altKey ) {
                                          deleteOrUndeleteYnItemType( event, ynItemType );

                                          nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                      }
                                      else {
                                          for ( const dPanelId of Object.keys( nodeButtonPanels.current ) ) {
                                              if ( dPanelId !== key2 ) {
                                                  nodeButtonPanels.current[ dPanelId ].collapse( event );
                                              }
                                              else {
                                                  nodeButtonPanels.current[ dPanelId ].toggle( event );
                                              }
                                          }
                                      }
                                  }}
                              />
                          }

                          toggleable={true}
                          collapsed={true}
                          className="yn-confirm-option-panel"
                          pt={{
                              root   : {
                                  style: {
                                      padding: '0'
                                  }
                              },
                              content: {
                                  className: 'yn-confirm-option-panel-content'
                              }
                          }}
                      >
                          <label>
                              <span className="pi pi-question-circle"></span>
                              {__( 'YN.base.frontend.metaModelDesigner.itemType.delete.confirmation', { itemType: ynItemType.label } )}
                          </label>
                          <Button
                              label={__( 'YN.base.common.no' )}
                              className="p-button-sm p-button-secondary mr-3"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={( event ) => {
                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                          <Button
                              label={__( 'YN.base.common.yes' )}
                              className="p-button-sm p-confirm-popup-accept"
                              rounded={false}
                              text={false}
                              outlined={true}
                              onClick={( event ) => {
                                  deleteOrUndeleteYnItemType( event, ynItemType );

                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                              }}
                          />
                      </Panel>
                  );

                  return <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={node => {
                          if ( node ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: node
                              };
                          }
                      }}

                      icon="pi pi-minus-circle yn-delete"
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      content={
                          <Panel
                              style={{ width: '25rem' }}
                              className="yn-confirm-option-panel"
                              pt={{
                                  content: {
                                      style: {
                                          padding: '.5rem 1rem'
                                      }
                                  }
                              }}
                          >
                              {buttonContent}
                          </Panel>
                      }
                  />;
              }
          },
          buildDeleteYnReferenceSetButton = ( ynReferenceSet : YnReferenceSet ) => {
              const buttonId = `${uid}_rS_${ynReferenceSet.id}_deleteButton`;

              nodeButtons.current[ buttonId ] = {
                  justInteracted: false
              };

              if ( readOnly || !ynReferenceSet.deletable ) {
                  return <Button
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: undefined
                              };
                          }
                      }}

                      icon={`pi ${ynReferenceSet.deleted ? 'pi-plus-circle' : 'pi-minus-circle'} yn-deleted`}
                      rounded={true}
                      text={true}
                      disabled={true}
                  />;
              }
              else {
                  return <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={`pi ${ynReferenceSet.deleted ? 'pi-plus-circle yn-dirty' : 'pi-minus-circle yn-delete'}`}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      onExpand={( event ) => {
                          let innerRetVal : boolean = true;

                          if ( event.altKey || ynReferenceSet.deleted ) {
                              deleteOrUndeleteYnReferenceSet( event, ynReferenceSet );

                              innerRetVal = false;
                          }

                          ignoreSelectionChange.current = true;

                          event.stopPropagation();
                          event.preventDefault();

                          return innerRetVal;
                      }}

                      content={!ynReferenceSet.deleted
                          ? <Panel
                              key={`${buttonId}_panel`}
                              id={`${buttonId}_panel`}

                              style={{ width: '25rem' }}
                              className="yn-confirm-option-panel"
                          >
                              <label>
                                  <span className="pi pi-question-circle yn-blue"></span>
                                  {__( 'YN.base.frontend.metaModelDesigner.referenceSet.delete.text', { referenceSet: ynReferenceSet.fullLabel } )}
                              </label>
                              <Button
                                  label={__( 'YN.base.common.no' )}
                                  className="p-button-sm p-button-secondary mr-3"
                                  rounded={false}
                                  text={false}
                                  outlined={true}
                                  onClick={() => {
                                      ignoreSelectionChange.current = true;

                                      nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                  }}
                              />
                              <Button
                                  label={__( 'YN.base.common.yes' )}
                                  className="p-button-sm p-confirm-popup-accept"
                                  rounded={false}
                                  text={false}
                                  outlined={true}
                                  onClick={( event ) => {
                                      deleteOrUndeleteYnReferenceSet( event, ynReferenceSet );

                                      ignoreSelectionChange.current = true;

                                      nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                  }}
                              />
                          </Panel>
                          : null}
                  />;
              }
          },
          buildTreeData                   = ( ynItemTypesToUse : YnItemType[], ynReferenceSetsToUse : YnReferenceSet[] ) : ExtendTreeNode[] => {
              const retVal : ExtendTreeNode[] = [];

              for ( const ynItemType of ynItemTypesToUse ) {
                  retVal.push( {
                      id              : `iT_${ynItemType.id}`,
                      key             : `iT_${ynItemType.id}`,
                      ynItemTypeItemId: ynItemType.id,
                      label           : ynItemType.label,
                      className       : 'yn-designertree-node',
                      icon            : parse( ynItemType.icon ),
                      deleted         : ynItemType.deleted || ( !!application && applicationToYnItemTypeItemIdMapping[ application ]?.[ ynItemType.id ] === false ),
                      dirty           : ynItemType.dirty,
                      error           : ynItemType.error
                          ? {
                              count  : ynItemType.error.count,
                              tooltip: ynItemType.error?.tooltip ? parse( ynItemType.error.tooltip ) : undefined
                          }
                          : undefined,
                      buttons         : [
                          buildAddYnReferenceSetButton( ynItemType ),
                          buildDeleteYnItemTypeButton( ynItemType )
                      ],
                      children        : ynReferenceSetsToUse.reduce(
                          ( acc, ynReferenceSet ) => {
                              if ( ynReferenceSet.sourceId === ynItemType.id || ynReferenceSet.targetId === ynItemType.id ) {
                                  acc.push( {
                                      id              : `rS_${ynReferenceSet.id}`,
                                      key             : `rS_${ynReferenceSet.id}`,
                                      ynReferenceSetId: ynReferenceSet.id,
                                      label           : ynReferenceSet.fullLabel,
                                      className       : 'yn-designertree-node',
                                      //icon            : parse( ynReferenceSet.icon ),
                                      deleted: ynReferenceSet.deleted,
                                      dirty  : ynReferenceSet.dirty,
                                      error  : ynReferenceSet.error
                                          ? {
                                              count  : ynReferenceSet.error.count,
                                              tooltip: ynReferenceSet.error?.tooltip ? parse( ynReferenceSet.error.tooltip ) : undefined
                                          }
                                          : undefined,
                                      buttons: [
                                          buildDeleteYnReferenceSetButton( ynReferenceSet )
                                      ]
                                  } )
                              }
                              return acc;
                          },
                          [] as ExtendTreeNode[]
                      ).sort( ( nodeData1, nodeData2 ) => {
                          if ( ( nodeData1.label ?? '' ) > ( nodeData2.label ?? '' ) ) {
                              return 1;
                          }
                          else if ( ( nodeData2.label ?? '' ) > ( nodeData1.label ?? '' ) ) {
                              return -1;
                          }
                          else {
                              return 0;
                          }
                      } )
                  } );
              }

              // sort by label
              retVal.sort( ( nodeData1, nodeData2 ) => {
                  if ( ( nodeData1.label ?? '' ) > ( nodeData2.label ?? '' ) ) {
                      return 1;
                  }
                  else if ( ( nodeData2.label ?? '' ) > ( nodeData1.label ?? '' ) ) {
                      return -1;
                  }
                  else {
                      return 0;
                  }
              } );

              return retVal;
          },
          treeData                        = useMemo(
              () => {
                  let retVal = elusiveData?.treeData;

                  if (
                      !retVal
                      || application !== lastParams.current?.application
                      || filteredItemTypes !== lastParams.current?.filteredItemTypes
                      || filteredReferenceSets !== lastParams.current?.filteredReferenceSets
                  ) {
                      retVal = buildTreeData( filteredItemTypes, filteredReferenceSets );

                      dispatch( setElusiveData( uid, { treeData: retVal } ) );
                  }

                  return retVal;
              },
              [
                  application,
                  elusiveData?.treeData,
                  filteredItemTypes,
                  filteredReferenceSets
              ]
          ),
          setScrollPosition               = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  scrollElement.scrollLeft = scrollLeft;
                  scrollElement.scrollTop = scrollTop;
              }
          },
          getScrollPosition               = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  dispatch( setParams( uid, {
                      scrollTop : scrollElement.scrollTop ?? 0,
                      scrollLeft: scrollElement.scrollLeft ?? 0
                  } ) );
              }
          },
          debouncedGetScrollPosition      = _.debounce( getScrollPosition.bind( null ), 250 ),
          treeNodeTemplate                = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as unknown as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  let retVal : React.ReactNode         = null,
                      label : string | React.ReactNode = nodeData.label
                          ? nodeData.label
                          : __( 'YN.base.frontend.metaModelDesigner.nodeLabel.placeholder' ) ?? '';

                  if ( nodeData.deleted ) {
                      labelClassNames.push( 'yn-deleted' );
                  }

                  else if ( nodeData.error?.count ) {
                      labelClassNames.push( 'yn-error' );
                  }

                  else if ( nodeData.dirty ) {
                      labelClassNames.push( 'yn-dirty' );
                  }

                  if (
                      (
                          nodeData.ynItemTypeItemId
                          && highlightYnItemTypeId
                          && nodeData.ynItemTypeItemId === highlightYnItemTypeId
                      )
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      || (
                          nodeData.ynReferenceSetId
                          && highlightYnReferenceSetId
                          && nodeData.ynReferenceSetId === highlightYnReferenceSetId
                      )
                  ) {
                      if ( !classNames.includes( 'yn-designertree-node-highlighted' ) ) {
                          classNames.push( 'yn-designertree-node-highlighted' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-designertree-node-highlighted' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if (
                      (
                          nodeData.ynItemTypeItemId
                          && selectedYnItemTypeId
                          && nodeData.ynItemTypeItemId === selectedYnItemTypeId
                      )
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      || (
                          nodeData.ynReferenceSetId
                          && selectedYnReferenceSetId
                          && nodeData.ynReferenceSetId === selectedYnReferenceSetId
                      )
                  ) {
                      if ( !classNames.includes( 'yn-designertree-node-selected' ) ) {
                          classNames.push( 'yn-designertree-node-selected' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-designertree-node-selected' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if ( !nodeData.label ) {
                      labelClassNames.push( 'yn-label-placeholder' );
                  }

                  if ( !isInvalidHTML( label as string ) ) {
                      label = parse( label as string );
                  }

                  // build node
                  retVal = <>
                      {nodeData.error?.tooltip &&
                          <Tooltip
                              target={`#${nodeData.key}_header`}
                              position="right"
                              className="yn-tooltip"
                          >
                              {nodeData.error.tooltip}
                          </Tooltip>
                      }
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                      <div
                          id={`${nodeData.key}_header`}
                          className="flex flex-auto overflow-hidden yn-designertree-header"
                          draggable="false"
                          onClick={() => {
                              if ( !nodeData.deleted ) {
                                  if ( nodeData.ynItemTypeItemId ) {
                                      onSelectYnItemTypeId?.( nodeData.ynItemTypeItemId );
                                      onSelectYnReferenceSetId?.();
                                  }
                                  else {
                                      onSelectYnReferenceSetId?.( nodeData.ynReferenceSetId );
                                      onSelectYnItemTypeId?.();
                                  }
                              }
                          }}
                          onMouseEnter={() => {
                              if ( !nodeData.deleted ) {
                                  if ( nodeData.ynItemTypeItemId ) {
                                      onHighlightYnItemTypeId?.( nodeData.ynItemTypeItemId );
                                      onHighlightYnReferenceSetId?.();
                                  }
                                  else {
                                      onHighlightYnReferenceSetId?.( nodeData.ynReferenceSetId );
                                      onHighlightYnItemTypeId?.();
                                  }
                              }
                          }}
                          onMouseLeave={() => {
                              onHighlightYnItemTypeId?.();
                              onHighlightYnReferenceSetId?.();
                          }}
                      >
                          <span key="label" className={labelClassNames.join( ' ' )}>{label}</span>
                          {nodeData.buttons}
                      </div>
                  </>;

                  nodeData.className = classNames.join( ' ' );

                  return retVal;
              },
              [
                  dataContext,
                  highlightYnItemTypeId,
                  highlightYnReferenceSetId,
                  language,
                  messageCatalogTimestamp,
                  onHighlightYnItemTypeId,
                  onSelectYnItemTypeId,
                  selectedYnItemTypeId,
                  selectedYnReferenceSetId
              ]
          ),
          onTreeNodeToggleClick           = useCallback(
              ( nodeKey : string ) => {
                  const newExpandedKeys = { ...expandedKeys };

                  if ( newExpandedKeys[ nodeKey ] ) {
                      delete newExpandedKeys[ nodeKey ];
                  }
                  else {
                      newExpandedKeys[ nodeKey ] = true;
                  }

                  dispatch( setParams( uid, { expandedKeys: newExpandedKeys } ) );
              },
              [
                  expandedKeys,
                  uid
              ]
          ),
          treeTogglerTemplate             = useCallback(
              ( treeNodeData : TreeNode.TreeNode ) : React.ReactNode => {
                  const nodeData = treeNodeData as unknown as ExtendTreeNode;

                  if ( nodeData.ynItemTypeItemId ) {
                      const icon = IconUtils.getJSXIcon(
                          expandedKeys?.[ nodeData.key ]
                              ? <ChevronDownIcon/>
                              : <ChevronRightIcon/>,
                          {},
                          {}
                      );

                      return <Button
                          className={`yn-tree-toggler p-tree-toggler p-link p-unselectable-text ${nodeData.selectable === false ? 'yn-disabled' : ''}`}
                          icon={icon}
                          disabled={nodeData.deleted}
                          onClick={() => {
                              onTreeNodeToggleClick( nodeData.key );
                          }}>
                      </Button>
                  }
                  else {
                      return <span className="yn-tree-toggler-placeholder"></span>;
                  }
              },
              [
                  onTreeNodeToggleClick,
                  expandedKeys
              ]
          ),
          onClear                         = useCallback(
              () => {
                  dispatch( setParam( uid, 'searchString', undefined ) );
              },
              [
                  uid
              ]
          ),
          onChange                        = useCallback(
              ( newSearchString : string ) => {
                  dispatch( setParam( uid, 'searchString', newSearchString ) );
              },
              [
                  uid
              ]
          ),
          onSearch                        = useCallback(
              () => {

              },
              []
          ),
          applicationDropdown             = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( showApplicationDropdown ) {
                      const emptyEntry = __( 'YN.base.frontend.metaModelDesigner.filter.application.emptyEntry' );

                      retVal = <Dropdown
                          value={application}
                          options={[ emptyEntry ].concat( applications )}
                          onChange={( e : any ) => {
                              if ( e.value === emptyEntry ) {
                                  onSelectApplication?.( undefined );
                              }
                              else {
                                  onSelectApplication?.( e.value );
                              }
                          }}
                          className="yn-application-dropdown"
                          style={{ width: '100%' }}
                          placeholder={__( 'YN.base.frontend.metaModelDesigner.filter.application.placeholder' )}
                      />;
                  }

                  return retVal;
              },
              [
                  application,
                  applications,
                  language,
                  messageCatalogTimestamp,
                  showApplicationDropdown,
                  onSelectApplication
              ]
          ),
          searchStringField               = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( showSearchStringField ) {
                      retVal = <SearchField
                          uid="pageQuickSearch"

                          value={searchString ? searchString : undefined}

                          onSearch={onSearch}
                          onClear={onClear}
                          onChange={onChange}

                          placeholder={__( 'YN.base.frontend.metaModelDesigner.filter.searchString.placeholder' )}
                          style={{ display: 'inline-block', width: '100%', marginTop: '.5rem', marginBottom: '0' }}
                      />;
                  }

                  return retVal;
              },
              [
                  language,
                  messageCatalogTimestamp,
                  searchString,
                  showSearchStringField,
                  uid
              ]
          ),
          tree                            = useMemo(
              () => {
                  return <Tree
                      key={`${uid}_tree`}
                      id={`${uid}_tree`}

                      ref={el => treeElement.current = el?.getElement() ?? null}

                      value={treeData}
                      nodeTemplate={treeNodeTemplate}

                      selectionKeys={{}}
                      expandedKeys={expandedKeys}
                      togglerTemplate={treeTogglerTemplate}
                      // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                      onToggle={() => {
                      }}

                      className="yn-menu-tree yn-node-content-tree yn-designertree"
                      style={{ position: 'relative', overflow: 'hidden', marginBottom: '.5rem' }}

                      pt={{
                          container: {
                              onScroll: debouncedGetScrollPosition
                          },
                          footer   : {
                              style: {
                                  margin: '1rem -1rem -1rem -1rem',
                                  width : 'calc(100% + 2rem)',
                                  zIndex: '10'
                              }
                          }
                      }}
                  />;
              },
              [
                  expandedKeys,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  uid
              ]
          ),
          content                         = useMemo(
              () => {
                  return <>
                      <div
                          className="yn-designertree-filter"
                      >
                          {applicationDropdown}
                          {searchStringField}
                      </div>
                      {tree}
                  </>;
              },
              [
                  applicationDropdown,
                  searchStringField,
                  tree
              ]
          );

    lastParams.current = {
        application,
        filteredItemTypes,
        filteredReferenceSets,
        highlightYnItemTypeId,
        highlightYnReferenceSetId,
        language,
        messageCatalogTimestamp,
        metaModelTimestamp,
        selectedYnItemTypeId,
        selectedYnReferenceSetId
    };

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {} )
    );

    // set the scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop,
            initialRendered.current
        ]
    );

    return content;
} );
DesignerTree.defaultProps = defaultProps;
DesignerTree.displayName = 'MetaModelDesigner-DesignerTree';

export default DesignerTree;
